import React from "react";
import { PointsWrapper, Parah, SubTitle, PointsIcon } from "../ContentsStyles";
import { ParahLink, ParahScrollLink } from "../../Pages/PagesStyles";

const TAC_4 = () => {
  return (
    <div>
      <SubTitle>Our Platform and business model</SubTitle>
      <Parah>
        We and the companies affiliated with us (such as Talents Explore Inc.
        and Talents Explore Ltd.) (“Affiliates”) provide the Platform so
        everyone can use it, in multiple languages around the world.
      </Parah>
      <Parah>
        The Platform allows you and others to create, view, interact with, and
        share content, and interact with others. We also personalise your
        experience on the Platform, such as providing your “For You” feed in the
        Talents Explore app, which shows you content based on what we have
        determined could be interesting for you.
      </Parah>
      <Parah>
        We want the Platform to be a safe place, so people can be who they are
        and have fun. We work with our Affiliates and use a mix of technology
        (including through automated means), human moderation, and reports from
        our users to identify infringements of and enforce these Terms and our
        Community Guidelines in order to protect you and all of our users.
      </Parah>
      <Parah>
        We don't charge you a fee to use most of the features of the Platform.
        Instead, businesses and organisations pay us to show you ads for their
        products and services
      </Parah>
      <Parah>
        Information on how we use data that we collect about you can be found in
        our <ParahLink to="/privacy-policy">Privacy Policy</ParahLink> and{" "}
        <ParahLink to="/cookies-policy">Cookies Policy.</ParahLink> These do not
        form part of this contract, but are important documents which we
        strongly recommend that you read.
      </Parah>
      <Parah>
        In short: You do not have to pay for your use of our Platform but, in
        exchange, we allow third parties to advertise or sell products to you
        and we receive payment for this.
      </Parah>
      <SubTitle>Account Details</SubTitle>
      <Parah>
        You can use some basic features of the Platform without having an
        account. If you use the Platform without an account, then these Terms
        will still apply to such use and we will still process your personal
        data in accordance with our{" "}
        <ParahLink to="/privacy-policy">Privacy Policy.</ParahLink>
      </Parah>
      <Parah>
        To access the full functionality of the Platform, you must create an
        account with us. We may offer different types of accounts.{" "}
      </Parah>
      <Parah>
        When you create an account, your account details will sync across the
        Platform, including across each Talents Explore app. For example, when
        you create an account via Talents Explore website, you will be able to
        access any other Talents Explore apps available in your country, using
        that account. Your account details, content and settings (including your
        privacy settings), and any changes you make, will sync across each
        Talents Explore app.
      </Parah>
      <Parah>
        When you create an account to access and use the Platform, you must
        provide accurate and up-to-date information about yourself (such as your
        date of birth). You agree to maintain and promptly update your details
        if they change.{" "}
        <ParahScrollLink smooth={true} duration={500} to="contactUs">
          Contact us{" "}
        </ParahScrollLink>{" "}
        for information on opening an account.
      </Parah>
      <Parah>
        It is important that you take reasonable steps to keep your account
        password confidential and that you do not disclose it to any third
        party. If you know or suspect that any third party knows your password
        or has accessed your account, please let us know straight away{" "}
        <ParahScrollLink smooth={true} duration={500} to="contactUs">
          here.
        </ParahScrollLink>
      </Parah>
      <Parah>
        In short: Your account is important. Keep it safe. Keep your details up
        to date. When you create an account in one Talents Explore app, you will
        be able to access other Talents Explore apps using that account
        (provided those apps are available in your country) and your account
        details, content and settings will sync across those apps.
      </Parah>
      <SubTitle>Minimum Age</SubTitle>
      <Parah>
        You can only use the Platform if you are 13 years of age or older. We
        monitor for underage use and we will terminate your account if we
        reasonably suspect that you are underage. You can appeal our decision to
        terminate your account if you think we have made a mistake about your
        age.
      </Parah>
      <Parah>In short: You need to be 13 or over to use our Platform.</Parah>
      <SubTitle>What You Can Do on the Platform</SubTitle>
      <Parah>Under these Terms, you can use the Platform in order to:</Parah>
      <PointsWrapper>
        <PointsIcon /> Create and share content
      </PointsWrapper>
      <PointsWrapper>
        <PointsIcon /> Interact with other users
      </PointsWrapper>
      <PointsWrapper>
        <PointsIcon /> View content created by others
      </PointsWrapper>
      <PointsWrapper>
        <PointsIcon /> Use the features and functionality on the Platform as
        provided to you from time to time.
      </PointsWrapper>
      <Parah>
        <ParahScrollLink smooth={true} duration={500} to="contactUs">
          Contact us
        </ParahScrollLink>{" "}
        for further information about the features of the Platform and how to
        use them.
      </Parah>
      <Parah>The permission we give to you:</Parah>
      <PointsWrapper>
        <PointsIcon /> Is limited to what we have said we will allow in these
        Terms
      </PointsWrapper>
      <PointsWrapper>
        <PointsIcon /> Is only for you
      </PointsWrapper>
      <PointsWrapper>
        <PointsIcon /> Cannot be given to anyone else by you
      </PointsWrapper>
      <PointsWrapper>
        <PointsIcon /> Can be withdrawn for the reasons allowed in these Terms
      </PointsWrapper>
      <Parah>
        Access to certain features of the Platform depends on your age. For
        example:
      </Parah>
      <PointsWrapper>
        <PointsIcon /> Direct Messaging: You must be aged 16 or older to use the
        direct messaging functionality
      </PointsWrapper>
      <Parah>
        In short: You can do many things on our Platform but you might not be
        able to use every feature if you are not old enough.
      </Parah>
      <SubTitle>What You Can’t Do on the Platform</SubTitle>
      <Parah>
        Our{" "}
        <ParahLink to="/community-guidelines"> Community Guidelines</ParahLink>{" "}
        apply to everyone and to all content on the Platform. If you want to use
        the Platform, you can’t create, post, share, link to or otherwise
        interact with content in breach of our Community Guidelines.
      </Parah>
      <Parah>In any event, you must not use the Platform to:</Parah>
      <PointsWrapper>
        <PointsIcon /> Do anything illegal
      </PointsWrapper>
      <PointsWrapper>
        <PointsIcon />
        Do anything that violates applicable anti-money laundering, counter
        terrorist financing, export controls and economic sanctions laws or
        regulations
      </PointsWrapper>
      <PointsWrapper>
        <PointsIcon /> Negatively impact the enjoyment of other users
      </PointsWrapper>
      <PointsWrapper>
        <PointsIcon /> Engage with minors in an exploitative or inappropriate
        way
      </PointsWrapper>
      <PointsWrapper>
        <PointsIcon /> Undermine the Platform’s operations or security
      </PointsWrapper>
      <PointsWrapper>
        <PointsIcon />
        Extract any data or content from the Platform using any automated system
        or software
      </PointsWrapper>
      <PointsWrapper>
        <PointsIcon /> Use or attempt to use another user’s account without
        authorisation.
      </PointsWrapper>
      <Parah>
        You must also not post, live stream or otherwise distribute any content
        on the Platform which:
      </Parah>
      <PointsWrapper>
        <PointsIcon />
        Infringes anyone else’s rights (such as intellectual property, privacy
        and/or personality rights of living or deceased people)
      </PointsWrapper>
      <PointsWrapper>
        <PointsIcon />
        Constitutes, Encourages or provides instructions for a criminal offence,
        or dangerous activities that may lead to serious injury or death or
        self-harm
      </PointsWrapper>
      <PointsWrapper>
        <PointsIcon />
        Spreads harmful misinformation such as misinformation that incites hate
        or prejudice or that misleads about or improperly influences elections
        or other civic processes
      </PointsWrapper>
      <PointsWrapper>
        <PointsIcon /> Contains a threat of any kind or which intimidates or
        harasses others, including posting any material that is intended to
        mock, humiliate, embarrass, intimidate, or hurt an individual
      </PointsWrapper>
      <PointsWrapper>
        <PointsIcon />
        Is obscene, pornographic, hateful or inflammatory or which promotes
        sexually explicit material (e.g. by linking to adult or pornographic
        websites)
      </PointsWrapper>
      <PointsWrapper>
        <PointsIcon />
        Contains or promotes violence or discrimination based on race,
        ethnicity, national origin, religion, caste, sexual orientation, sex,
        gender identity, serious disease, disability, immigration status or age.
      </PointsWrapper>
      <PointsWrapper>
        <PointsIcon /> Otherwise contains harmful content (such as content that
        causes physical, mental or moral detriment to minors)
      </PointsWrapper>
      <Parah>
        If you post content on the Platform about a brand or a product in return
        for any payment or other incentive, you must comply with the{" "}
        <ParahLink to="/branded-content-policy">
          Branded Content Policy
        </ParahLink>
        . You must use appropriate disclosures including any functionality the
        Platform provides to ensure that any posts that are, for example,
        sponsored are clear. For more details about Branded Content on the
        Platform, <ParahLink to="/branded-content-policy">see here</ParahLink>.
        In addition, in relation to any such content you must not:
      </Parah>
      <PointsWrapper>
        <PointsIcon />
        Use surreptitious or subliminal techniques
      </PointsWrapper>
      <PointsWrapper>
        <PointsIcon /> Prejudice respect for human dignity
      </PointsWrapper>
      <PointsWrapper>
        <PointsIcon /> Include or promote any discrimination based on sex,
        racial or ethnic origin, nationality, religion or belief, disability,
        age or sexual orientation
      </PointsWrapper>
      <PointsWrapper>
        <PointsIcon /> Encourage behaviour prejudicial to health or safety or
        which is grossly prejudicial to the protection of the environment
      </PointsWrapper>
      <PointsWrapper>
        <PointsIcon /> Cause physical, mental or moral detriment to minors
      </PointsWrapper>
      <PointsWrapper>
        <PointsIcon />
        Directly exhort minors to buy or hire a product or service or directly
        encourage minors to persuade their parents or others to purchase goods
        or services being advertised
      </PointsWrapper>
      <PointsWrapper>
        <PointsIcon /> Exploit the special trust minors place in parents,
        teachers or other persons, or show minors in dangerous situations.
      </PointsWrapper>
      <Parah>
        We respect copyright and other intellectual property rights. Please read
        our{" "}
        <ParahLink to="/intellectual-property-policy">
          Intellectual Property Policy
        </ParahLink>
        .{" "}
      </Parah>
      {/* You can report suspected infringements of intellectual property rights on
      the Platform here:
      <PointsWrapper>
        <PointsIcon />
        Copyright: https://www.talentsexplore.com/legal/report/Copyright
      </PointsWrapper>
      <PointsWrapper>
        <PointsIcon />
        Trademark: https://www.talentsexplore.com/legal/report/Trademark
      </PointsWrapper> */}
      <Parah>
        You can report suspected illegal content or content which otherwise
        breaches these Terms or our Community Guidelines through the reporting
        functionalities provided on the Platform or here.
      </Parah>
      <Parah>
        In short: Enjoy using our Platform but, for the benefit of all our
        users, there are rules you need to follow. If you see something that
        should not be on our Platform, please tell us.
      </Parah>
      <SubTitle>Your Content</SubTitle>
      <Parah>
        It is important that you understand what happens to the content that you
        create, post or share on the Platform:
      </Parah>
      <PointsWrapper>
        <PointsIcon /> You are responsible for the content that you make
        available on the Platform and you should have all the rights needed to
        create, post or share content on the Platform.
      </PointsWrapper>
      <PointsWrapper>
        <PointsIcon /> When you create, post or share content via one Talents
        Explore app, that content may be automatically posted and shared across
        other Talents Explore apps (although your privacy settings will still
        apply across each app). For example, when you post a video on Talents
        Explore website, that video will also be posted and available on Talents
        Explore App.
      </PointsWrapper>
      <PointsWrapper>
        <PointsIcon />
        <div>
          We may remove or restrict access to any content, including yours, if
          we reasonably believe (i) it is in breach of these Terms or our{" "}
          <ParahLink to="/community-guidelines">Community Guidelines</ParahLink>
          ; or (ii) it causes harm to us, Affiliates, our users or other third
          parties.
        </div>
      </PointsWrapper>
      <PointsWrapper>
        <PointsIcon /> If we remove or restrict access to content, we will
        notify you without undue delay and state the reasons for our decision,
        unless it is not appropriate for us to do so or we reasonably believe
        that doing so would cause damage to us, Affiliates, our users or other
        third parties, or we are legally prevented from doing so. If you think
        we have made a mistake in removing or restricting access to your
        content, you can appeal through the appeal functionalities provided on
        the Platform and we will review our decision and decide again.
      </PointsWrapper>
      <PointsWrapper>
        <PointsIcon /> You are free to remove your content from the Platform at
        any time. When you remove your content from one Talents Explore Website,
        it will also be removed from other Talents Explore apps.
      </PointsWrapper>
      <PointsWrapper>
        <PointsIcon /> Depending on your Platform settings, if other users of
        the Platform have used your content to create new content (e.g. by using
        Duet or Stitch) or shared your content on third party services, then
        that new content may stay on the Platform or those third party services
        even if you subsequently delete your content or your account. You may
        make a separate request for Duet or Stitch videos that contain your
        content to be deleted.
      </PointsWrapper>
      <PointsWrapper>
        <PointsIcon /> You can restrict how other users interact with and use
        your content in your Platform settings. You should familiarise yourself
        with these settings before you post content on the Platform.
      </PointsWrapper>
      <SubTitle>Ownership of Content and Grant of Licences</SubTitle>
      <Parah>
        We don’t own your content. If you are the owner of the intellectual
        property rights in the content that you make available on the Platform,
        then nothing in these Terms changes that.
      </Parah>
      <Parah>
        To provide the Platform, we need certain rights from you (called a
        licence). The details of these licences are set out below.
      </Parah>
      <Parah>
        By creating, posting or otherwise making content available on the
        Platform, you grant to Talents Explore a:
      </Parah>
      <PointsWrapper>
        <PointsIcon /> Non-exclusive (which means that you can licence your
        content to others)
      </PointsWrapper>
      <PointsWrapper>
        <PointsIcon /> Royalty-free (which means that we don’t pay you for this
        licence)
      </PointsWrapper>
      <PointsWrapper>
        <PointsIcon /> Transferable (which means that we can give the rights you
        give us to someone else)
      </PointsWrapper>
      <PointsWrapper>
        <PointsIcon />
        Sub-licensable (which means that we can licence your content to others,
        e.g. to service providers that help us to provide the Platform or to
        trusted third parties that have entered into agreements with us to
        operate, develop and provide the Platform) and
      </PointsWrapper>
      <PointsWrapper>
        <PointsIcon />
        Worldwide (which means that the licence applies anywhere in the world)
      </PointsWrapper>
      <Parah>
        licence to use your content, including to reproduce (e.g. to copy),
        adapt or make derivative works (e.g. to translate and/or create
        captions), perform and communicate your content to the public (e.g. to
        display it), for the purposes of operating, developing and providing the
        Platform, subject to your Platform settings.
      </Parah>
      <Parah>
        The licence to your content that you grant to us extends to Affiliates
        as part of making the Platform available.
      </Parah>
      <Parah>
        You also grant to each user of the Platform a non-exclusive,
        royalty-free, worldwide licence to access and use your content,
        including to reproduce (e.g. to copy, share or download), adapt or make
        derivative works (e.g. to include your content in their content) perform
        and communicate that content to the public (e.g. to display it) using
        the features and functions of the Platform for entertainment purposes,
        subject to your Platform settings.
      </Parah>
      <Parah>
        Your licences to Talents Explore and to users end when you close your
        account or when you or we remove your content from the Platform in
        accordance with these Terms. However, due to the nature of the Platform
        and our legal obligations, the licence granted will continue after you
        have removed your content to the extent that:
      </Parah>
      <PointsWrapper>
        <PointsIcon />
        You have allowed, via your Platform settings, other users of the
        Platform to use or reuse your content (e.g. by using Duet, Stitch,
        download or share functionalities); or{" "}
      </PointsWrapper>
      <PointsWrapper>
        <PointsIcon />
        We are obliged to store or process your content for legal reasons.
      </PointsWrapper>
      <Parah>
        If you choose to submit comments, ideas or feedback to us, you agree
        that we are free to use them for the purposes of operating, developing,
        improving and providing the Platform without compensation to you.
      </Parah>
      <Parah>
        In short: When you post content to the Platform, it remains yours, but
        we can show it to other users and use it to provide the Platform and
        other users may be able to use it too. That is what our Platform is all
        about. If you later take it down, copies of it made by other users may
        remain on the Platform.
      </Parah>
    </div>
  );
};

export default TAC_4;
